import { useCallback, useEffect, useRef, useState } from "react";
import { requester } from "api/pesabackend";
import usePaginatedList, { DATE_FILTERS } from "api/hooks/shared/paginatedList";

// ── Session list (paginated, date-filtered) ──────────────────────────────────

export const useStockCountSessionList = (branchId, { statusFilter = "all", search = "" } = {}) => {
  const extraParams = {};
  if (statusFilter && statusFilter !== "all") extraParams.status = statusFilter;
  if (search) extraParams.search = search;

  const {
    rows: list,
    count,
    loading,
    loadingMore,
    hasMore,
    loadMore,
    dateFilter,
    setDateFilter,
    customRange,
    setCustomRange,
    applyCustomRange,
    refresh,
  } = usePaginatedList({
    path: "/inventory/stock_count/list",
    branchId,
    branchParam: "business_branch",
    pageSize: 50,
    extraParams,
  });

  return {
    list,
    count,
    isLoadingList: loading,
    isLoadingMore: loadingMore,
    hasMore,
    loadMore,
    dateFilter,
    setDateFilter,
    customRange,
    setCustomRange,
    applyCustomRange,
    refresh,
  };
};

// ── Single session detail ────────────────────────────────────────────────────

export const useStockCountSession = (sessionId) => {
  const [session, setSession] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const fetch = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    setError(false);
    try {
      const res = await requester.get(`/inventory/stock_count/${sessionId}`);
      setSession(res.data.data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { session, isLoading, error, refresh: fetch };
};

// ── Lines with variance filter + load more ──────────────────────────────────

export const useStockCountLines = (sessionId, varianceFilter = "all") => {
  const [lines, setLines] = useState([]);
  const [count, setCount] = useState(0);
  const [nextUrl, setNextUrl] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const [isLoadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);

  const prevSession = useRef(null);
  const prevVariance = useRef(null);

  const buildUrl = useCallback(
    (page = 1) =>
      `/inventory/stock_count/${sessionId}/lines?variance=${varianceFilter}&page=${page}&page_size=50`,
    [sessionId, varianceFilter]
  );

  const parseNext = (next) => {
    if (!next) return null;
    try {
      const url = new URL(next);
      return url.pathname.replace(/^\/api/, "") + url.search;
    } catch {
      return next.startsWith("/") ? next : null;
    }
  };

  const fetchFirst = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    setError(false);
    try {
      const res = await requester.get(buildUrl(1));
      const payload = res.data;
      setLines(payload.data ?? []);
      setCount(payload.count ?? 0);
      setNextUrl(parseNext(payload.next));
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [sessionId, buildUrl]);

  useEffect(() => {
    if (sessionId !== prevSession.current || varianceFilter !== prevVariance.current) {
      prevSession.current = sessionId;
      prevVariance.current = varianceFilter;
      fetchFirst();
    }
  }, [sessionId, varianceFilter, fetchFirst]);

  const loadMore = useCallback(async () => {
    if (!nextUrl || isLoadingMore) return;
    setLoadingMore(true);
    try {
      const res = await requester.get(nextUrl);
      const payload = res.data;
      setLines((prev) => [...prev, ...(payload.data ?? [])]);
      setNextUrl(parseNext(payload.next));
    } catch {
      setError(true);
    } finally {
      setLoadingMore(false);
    }
  }, [nextUrl, isLoadingMore]);

  return {
    lines,
    count,
    isLoading,
    isLoadingMore,
    hasMore: !!nextUrl,
    loadMore,
    error,
    refresh: fetchFirst,
  };
};

export { DATE_FILTERS };
